import YAML from 'yaml'
import type { RenderData } from '../assetTypes'
import { collectAssetReferencesFromValue, extractAssetReferences } from '../shared/assetReferences'
import { cloneJsonObject, normalizeJsonObject, type JsonObject } from '../shared/jsonValues'

const ASSET_REFERENCE_PATTERN = /^asset:\/\/([0-9a-fA-F-]{36})$/

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value)

const normalizeUnitKey = (value: string) => value.trim().toLowerCase()

const normalizeAssetReference = (value: string) => {
  const trimmed = value.trim()
  const match = trimmed.match(ASSET_REFERENCE_PATTERN)
  if (!match?.[1]) {
    throw new Error(`Learning template reference "${value}" must use the form asset://<uuid>.`)
  }
  return `asset://${match[1].toLowerCase()}`
}

const cloneResolvedUnit = (unit: ResolvedLearningBatteryUnit): ResolvedLearningBatteryUnit => ({
  key: unit.key,
  template: unit.template,
  params: cloneJsonObject(unit.params),
})

export type LearningBatteryUnitDefinition = {
  key: string
  template: string
  params: JsonObject
}

export type LearningBatteryDefinition = {
  root: string
  units: LearningBatteryUnitDefinition[]
}

export type ResolvedLearningBatteryUnit = {
  key: string
  template: string
  params: JsonObject
}

export type ResolvedLearningBatteryDefinition = {
  root: ResolvedLearningBatteryUnit
  units: ResolvedLearningBatteryUnit[]
  resolveUnit: (key: string) => ResolvedLearningBatteryUnit
}

const parseUnitDefinition = (value: unknown, index: number): LearningBatteryUnitDefinition => {
  if (!isRecord(value)) {
    throw new Error(`Unit entry #${index + 1} must be a YAML object.`)
  }

  const key = typeof value.key === 'string' ? value.key.trim() : ''
  if (!key) {
    throw new Error(`Unit entry #${index + 1} must define a non-empty key.`)
  }

  const rawTemplate = typeof value.template === 'string' ? value.template.trim() : ''
  if (!rawTemplate) {
    throw new Error(`Unit "${key}" must define a learning template reference.`)
  }

  const rawParams = value.params
  if (rawParams !== undefined && rawParams !== null && !isRecord(rawParams)) {
    throw new Error(`Unit "${key}" params must be a YAML object.`)
  }

  return {
    key,
    template: normalizeAssetReference(rawTemplate),
    params: normalizeJsonObject(rawParams as Record<string, unknown> | null | undefined),
  }
}

export const parseLearningBatteryDefinition = (content: string): LearningBatteryDefinition => {
  const parsed = YAML.parse(String(content || ''))
  if (!isRecord(parsed)) {
    throw new Error('Learning Battery payload must be a YAML object.')
  }

  const root = typeof parsed.root === 'string' ? parsed.root.trim() : ''
  if (!root) {
    throw new Error('Learning Battery payload must define a non-empty "root" unit key.')
  }

  if (!Array.isArray(parsed.units)) {
    throw new Error('Learning Battery payload must define "units" as a YAML list.')
  }

  const units = parsed.units.map((entry, index) => parseUnitDefinition(entry, index))
  const seenKeys = new Set<string>()

  units.forEach((unit) => {
    const normalizedKey = normalizeUnitKey(unit.key)
    if (seenKeys.has(normalizedKey)) {
      throw new Error(`Unit keys must be unique. Duplicate unit "${unit.key}".`)
    }
    seenKeys.add(normalizedKey)
  })

  return {
    root,
    units,
  }
}

export const resolveLearningBatteryDefinition = (content: string): ResolvedLearningBatteryDefinition => {
  const learningBattery = parseLearningBatteryDefinition(content)
  const unitsByKey = new Map<string, ResolvedLearningBatteryUnit>()

  learningBattery.units.forEach((unit) => {
    unitsByKey.set(normalizeUnitKey(unit.key), {
      key: unit.key,
      template: unit.template,
      params: cloneJsonObject(unit.params),
    })
  })

  const resolveUnit = (key: string): ResolvedLearningBatteryUnit => {
    const unit = unitsByKey.get(normalizeUnitKey(key))
    if (!unit) {
      throw new Error(`Unknown unit "${key}".`)
    }
    return cloneResolvedUnit(unit)
  }

  const rootUnit = unitsByKey.get(normalizeUnitKey(learningBattery.root))
  if (!rootUnit) {
    throw new Error(`Learning Battery root "${learningBattery.root}" does not match any unit in the "units" list.`)
  }

  return {
    root: cloneResolvedUnit(rootUnit),
    units: learningBattery.units.map((unit) => resolveUnit(unit.key)),
    resolveUnit,
  }
}

export const collectLearningBatteryAssetReferences = (content: string, renderData?: RenderData) => {
  const matches = new Set<string>()

  renderData?.entity?.fields?.forEach((field) => {
    if (typeof field.value === 'string') {
      extractAssetReferences(field.value, matches)
    }
  })

  try {
    const resolved = resolveLearningBatteryDefinition(content)
    extractAssetReferences(resolved.root.template, matches)
    collectAssetReferencesFromValue(resolved.root.params).forEach((reference) => matches.add(reference))
  } catch {
    // Keep asset collection best-effort. The renderer surfaces parse/validation errors.
  }

  return Array.from(matches)
}
